import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Send as SendIcon,
  AutoAwesome as AIIcon,
  Schedule as ScheduleIcon,
  EventBusy as UnscheduleIcon,
  CancelOutlined as CancelIcon,
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { campaignApi } from '../../../api/marketing';
import { Campaign, CampaignStats } from '../../../types/marketing.types';

const CampaignsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openAIDialog, setOpenAIDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [sending, setSending] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState<{ open: boolean; campaign: Campaign | null; scheduledAt: string }>({
    open: false,
    campaign: null,
    scheduledAt: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    recipients: {
      type: 'ALL' as 'ALL' | 'TAGS',
      tags: [] as string[]
    }
  });
  const [aiPrompt, setAiPrompt] = useState({
    prompt: '',
    tone: 'professional' as 'professional' | 'casual' | 'technical' | 'friendly',
    includeCallToAction: true,
    targetAudience: '',
    goal: ''
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await campaignApi.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined
      });

      if (response.success && response.data) {
        setCampaigns(response.data);
        setTotalCount(response.pagination?.total || 0);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch campaigns', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, [fetchCampaigns]);

  const fetchStats = async () => {
    try {
      const response = await campaignApi.getStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleOpenDialog = (campaign?: Campaign) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name,
        subject: campaign.subject,
        content: campaign.content,
        recipients: {
          type: campaign.recipients.type,
          tags: campaign.recipients.tags || []
        }
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        name: '',
        subject: '',
        content: '',
        recipients: {
          type: 'ALL',
          tags: []
        }
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCampaign(null);
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };

      if (editingCampaign) {
        await campaignApi.update(editingCampaign._id, payload);
        enqueueSnackbar('Campaign updated successfully', { variant: 'success' });
      } else {
        await campaignApi.create(payload);
        enqueueSnackbar('Campaign created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to save campaign', { variant: 'error' });
    }
  };

  const handleGenerateAI = async () => {
    try {
      setGeneratingAI(true);
      const response = await campaignApi.generateWithAI({
        topic: aiPrompt.prompt,
        targetAudience: aiPrompt.targetAudience,
        goal: aiPrompt.goal
      });
      
      if (response.success && response.data) {
        enqueueSnackbar('Campaign content generated successfully!', { variant: 'success' });
        setOpenAIDialog(false);
        fetchCampaigns();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate campaign', { variant: 'error' });
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSend = async (id: string) => {
    if (!window.confirm('Are you sure you want to send this campaign? This action cannot be undone.')) {
      return;
    }

    try {
      setSending(true);
      await campaignApi.send(id);
      enqueueSnackbar('Campaign queued for delivery', { variant: 'success' });
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to send campaign', { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      await campaignApi.delete(id);
      enqueueSnackbar('Campaign deleted successfully', { variant: 'success' });
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to delete campaign', { variant: 'error' });
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDialog.campaign || !scheduleDialog.scheduledAt) return;
    try {
      await campaignApi.schedule(scheduleDialog.campaign._id, new Date(scheduleDialog.scheduledAt));
      enqueueSnackbar('Campaign scheduled', { variant: 'success' });
      setScheduleDialog({ open: false, campaign: null, scheduledAt: '' });
      fetchCampaigns();
    } catch (error) {
      enqueueSnackbar('Failed to schedule campaign', { variant: 'error' });
    }
  };

  const handleUnschedule = async (id: string) => {
    try {
      await campaignApi.unschedule(id);
      enqueueSnackbar('Campaign reverted to draft', { variant: 'success' });
      fetchCampaigns();
    } catch (error) {
      enqueueSnackbar('Failed to unschedule campaign', { variant: 'error' });
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this campaign? It cannot be sent afterwards.')) return;
    try {
      await campaignApi.cancel(id);
      enqueueSnackbar('Campaign cancelled', { variant: 'success' });
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to cancel campaign', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT':
        return 'success';
      case 'DRAFT':
        return 'default';
      case 'SCHEDULED':
        return 'info';
      case 'SENDING':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const textFieldStyles = {
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: 'white' }
    },
    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Email Campaigns</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={() => setOpenAIDialog(true)}
          >
            Generate with AI
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Campaigns
                    </Typography>
                    <Typography variant="h4">{stats.totalCampaigns}</Typography>
                  </Box>
                  <EmailIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Sent
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.sentCampaigns}
                    </Typography>
                  </Box>
                  <SuccessIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Sent
                    </Typography>
                    <Typography variant="h4">{stats.totalSent}</Typography>
                  </Box>
                  <SendIcon sx={{ fontSize: 40, color: 'info.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Avg Open Rate
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {stats.openRate}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="SENDING">Sending</MenuItem>
                <MenuItem value="SENT">Sent</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Campaigns Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Open Rate</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : campaigns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No campaigns found
                </TableCell>
              </TableRow>
            ) : (
              campaigns.map((campaign) => (
                <TableRow key={campaign._id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{campaign.subject}</TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.status}
                      color={getStatusColor(campaign.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{campaign.stats?.totalRecipients || 0}</TableCell>
                  <TableCell>{campaign.stats?.sent || 0}</TableCell>
                  <TableCell>
                    {campaign.stats?.opened && campaign.stats?.sent
                      ? `${((campaign.stats.opened / campaign.stats.sent) * 100).toFixed(1)}%`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(campaign.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {campaign.status === 'DRAFT' && (
                      <>
                        <Tooltip title="Send Now">
                          <IconButton
                            size="small"
                            onClick={() => handleSend(campaign._id)}
                            disabled={sending}
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Schedule">
                          <IconButton
                            size="small"
                            onClick={() => setScheduleDialog({ open: true, campaign, scheduledAt: '' })}
                          >
                            <ScheduleIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(campaign)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {campaign.status === 'SCHEDULED' && (
                      <Tooltip title="Unschedule — revert to draft">
                        <IconButton
                          size="small"
                          onClick={() => handleUnschedule(campaign._id)}
                        >
                          <UnscheduleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
                      <>
                        <Tooltip title="Cancel campaign">
                          <IconButton
                            size="small"
                            onClick={() => handleCancel(campaign._id)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(campaign._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* AI Generation Dialog */}
      <Dialog 
        open={openAIDialog} 
        onClose={() => setOpenAIDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Generate Campaign with AI</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Campaign Purpose / Topic"
              required
              fullWidth
              multiline
              rows={3}
              value={aiPrompt.prompt}
              onChange={(e) => setAiPrompt({ ...aiPrompt, prompt: e.target.value })}
              placeholder="e.g., Promote our new energy-saving service"
              sx={textFieldStyles}
            />
            <FormControl fullWidth sx={textFieldStyles}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Tone</InputLabel>
              <Select
                value={aiPrompt.tone}
                label="Tone"
                onChange={(e) => setAiPrompt({ ...aiPrompt, tone: e.target.value as any })}
                sx={{ color: 'white' }}
              >
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="friendly">Friendly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Target Audience Description"
              fullWidth
              value={aiPrompt.targetAudience}
              onChange={(e) => setAiPrompt({ ...aiPrompt, targetAudience: e.target.value })}
              placeholder="e.g., Small business owners interested in cost savings"
              sx={textFieldStyles}
            />
            <TextField
              label="Campaign Goal"
              required
              fullWidth
              value={aiPrompt.goal}
              onChange={(e) => setAiPrompt({ ...aiPrompt, goal: e.target.value })}
              placeholder="e.g., Promote winter energy-switching offer"
              sx={textFieldStyles}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAIDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleGenerateAI} 
            variant="contained" 
            disabled={!aiPrompt.prompt || !aiPrompt.goal || generatingAI}
            startIcon={generatingAI ? <CircularProgress size={20} /> : <AIIcon />}
          >
            {generatingAI ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Campaign Name"
              required
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Email Subject"
              required
              fullWidth
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Email Content"
              required
              fullWidth
              multiline
              rows={10}
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your email content here..."
              sx={textFieldStyles}
            />
            <TextField
              label="Target Tags (comma separated)"
              fullWidth
              value={formData.recipients.tags?.join(', ') || ''}
              onChange={(e) => setFormData({
                ...formData,
                recipients: {
                  ...formData.recipients,
                  type: e.target.value ? 'TAGS' : 'ALL',
                  tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                }
              })}
              helperText="Leave empty to send to all subscribers"
              sx={textFieldStyles}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={!formData.name || !formData.subject || !formData.content}
          >
            {editingCampaign ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog
        open={scheduleDialog.open}
        onClose={() => setScheduleDialog({ open: false, campaign: null, scheduledAt: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Schedule Campaign</DialogTitle>
        <DialogContent>
          <TextField
            label="Send at"
            type="datetime-local"
            fullWidth
            required
            value={scheduleDialog.scheduledAt}
            onChange={(e) => setScheduleDialog({ ...scheduleDialog, scheduledAt: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().slice(0, 16) }}
            helperText="The campaign sends automatically at this time."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog({ open: false, campaign: null, scheduledAt: '' })}>Cancel</Button>
          <Button
            onClick={handleSchedule}
            variant="contained"
            disabled={!scheduleDialog.scheduledAt}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignsPage;
